<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Budget Management</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Upload Budget</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer-wrapper">
        <div class="form-outer" style="width: 1400px;">
          <form @submit.prevent="handleSubmit">
            <div class="crud-form position-relative">

              <div class="form-inner">
                <div class="form-header d-flex justify-content-between align-items-center">
                  <h1 class="mb-0">Upload MDA Line Item Budget </h1>
                </div>

                <div class="form-body">
                  <div class="row mt-2 mb-3">
                    <!-- Upload File -->
                    <div v-if="!preview.length" class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Upload Excel File</label></div>
                      <div class="col-md-7 input-right d-flex align-items-center gap-2">

                        <!-- Hidden input -->
                        <input type="file" ref="fileInput"
                          accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          style="display: none !important" @change="handleFileSelect" />

                        <!-- Trigger -->
                        <button type="button" style="width: 100%" class="btn btn-secondary" @click="triggerFileInput">
                          Choose File
                        </button>

                        <!-- Upload -->
                        <button type="button" style="width: 100%"
                          class="btn btn-outline-primary d-flex align-items-center justify-content-center"
                          @click="uploadFile" :disabled="loadingPreview || !selectedFile">
                          <span class="material-symbols-outlined">upload</span>
                          {{ loadingPreview ? 'Uploading...' : 'Upload File' }}
                        </button>

                        <!-- Filename -->
                        <span v-if="selectedFileName" class="text-muted small">
                          {{ selectedFileName }}
                        </span>
                      </div>
                    </div>

                    <div class="preview-area position-relative">
                      <div v-if="loadingPreview" class="loading-state text-center py-5 position-relative">
                        <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                        <p class="mt-2 mb-0 text-muted">Loading preview...</p>
                      </div>

                      <div v-else-if="!loadingPreview && preview.length" class="preview mt-4 py-0">
                        <!-- Table Controls -->
                        <div class="table-controls mt-3">
                          <div class="budget-title">
                            <div v-if="form.fileEntryError" class="alert alert-danger mb-3" role="alert">
                              {{ form.errorMessage || 'There was an error processing the file.' }}
                            </div>
                            <div class="row mb-3">
                              <!-- File Name -->
                              <div class="d-flex justify-content-center align-items-center">
                                <h2 type="text" class="form-control view-only-input text-center budget-title" readonly>
                                  {{ form.fileName || 'N/A' }}
                                </h2>
                              </div>
                              <!-- Budget Name -->
                              <div class="col-md-12 row form-group position-relative floating-label-group">
                                <div class="col-md-2 label-left d-flex justify-content-end align-items-center ">
                                  <label for="budgetName" class="form-label">Budget Name</label>
                                </div>
                                <div class="col-md-10 input-right">
                                  <input id="budgetName" type="text" class="form-control" v-model="form.budgetName"
                                    placeholder="Enter budget name" required />
                                </div>
                              </div>
                              <!-- Budget Year -->
                              <div class="col-md-12 row form-group position-relative floating-label-group">
                                <div class="col-md-2 label-left d-flex justify-content-end align-items-center">
                                  <label for="year" class="form-label">Budget Year</label>
                                </div>
                                <div class="col-md-10 input-right">
                                  <input id="budgetYear" type="text" class="form-control form-view-only-input"
                                    :value="form.year || 'N/A'" placeholder="" readonly />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div class="table-controls-top">
                            <div class="table-controls-left">
                              <div class="entries-control">
                                <span>Show</span>
                                <select class="form-select form-select-sm" v-model.number="pageLength">
                                  <option :value="10">10</option>
                                  <option :value="25">25</option>
                                  <option :value="50">50</option>
                                  <option :value="100">100</option>
                                </select>
                                <span>Entries</span>
                              </div>

                              <div class="search-box" style="max-width: 100%;">
                                <span class="material-symbols-outlined search-icon">search</span>
                                <input type="search" v-model="search" class="form-control form-control-sm search-input"
                                  placeholder="Search by mda, economic line item, amount budgeted or revenue amount..." />
                              </div>

                              <div v-if="form.fileEntryError && showErrorsOnly" class="export-links">
                                <a href="#" class="export-link text-decoration-none"
                                  @click.prevent="exportToPDF()">Export to PDF</a>
                                <a href="#" class="export-link text-decoration-none"
                                  @click.prevent="exportToExcel()">Export to Excel</a>
                              </div>
                            </div>
                          </div>

                          <div v-if="form.fileEntryError" class="file-errors alert alert-danger mt-3" role="alert">
                            <div class="d-flex align-items-center gap-1">
                              <span class="error-icon material-symbols-outlined me-1 align-items-center">
                                warning
                              </span>
                              Please fix the errors in the file and re-upload to proceed: {{ numberOfFileErrors }}
                              errors found.
                            </div>
                            <div class="show-errors d-flex gap-2 mt-1">
                              <span v-if="!showErrorsOnly" class="text-decoration-underline" style="cursor: pointer;"
                                @click="showErrors">
                                Show Errors
                              </span>
                              <span v-if="showErrorsOnly" class="text-decoration-underline" style="cursor: pointer;"
                                @click="hideErrors">
                                Show All
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Table -->
                        <div class="table-wrapper mb-0">
                          <div class="table-container mb-0">
                            <table class="responsive-table">
                              <thead>
                                <tr>
                                  <th></th>
                                  <th class="sortable-header">
                                    <div class="th-content">
                                      <span>MDA</span>
                                      <div class="sort-icons">
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'mda' && sortOrder === 'asc' }"
                                          @click="setSort('mda', 'asc')">
                                          arrow_drop_up
                                        </span>
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'mda' && sortOrder === 'desc' }"
                                          @click="setSort('mda', 'desc')">
                                          arrow_drop_down
                                        </span>
                                      </div>
                                    </div>
                                  </th>
                                  <th class="sortable-header">
                                    <div class="th-content">
                                      <span>Economic Line Item</span>
                                      <div class="sort-icons">
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'economic_line_item' && sortOrder === 'asc' }"
                                          @click="setSort('economic_line_item', 'asc')">
                                          arrow_drop_up
                                        </span>
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'economic_line_item' && sortOrder === 'desc' }"
                                          @click="setSort('economic_line_item', 'desc')">
                                          arrow_drop_down
                                        </span>
                                      </div>
                                    </div>
                                  </th>
                                  <th class="sortable-header">
                                    <div class="th-content">
                                      <span>Amount Budgeted</span>
                                      <div class="sort-icons">
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'amount_budgeted' && sortOrder === 'asc' }"
                                          @click="setSort('amount_budgeted', 'asc')">
                                          arrow_drop_up
                                        </span>
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'amount_budgeted' && sortOrder === 'desc' }"
                                          @click="setSort('amount_budgeted', 'desc')">
                                          arrow_drop_down
                                        </span>
                                      </div>
                                    </div>
                                  </th>
                                  <th class="sortable-header">
                                    <div class="th-content">
                                      <span>Revenue Amount</span>
                                      <div class="sort-icons">
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'revenue_amount' && sortOrder === 'asc' }"
                                          @click="setSort('revenue_amount', 'asc')">
                                          arrow_drop_up
                                        </span>
                                        <span class="material-symbols-outlined sort-icon"
                                          :class="{ active: sortKey === 'revenue_amount' && sortOrder === 'desc' }"
                                          @click="setSort('revenue_amount', 'desc')">
                                          arrow_drop_down
                                        </span>
                                      </div>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(preview, index) in paginatedItems" :key="preview.id"
                                  :class="index % 2 === 0 ? 'row-even' : 'row-odd'"
                                  :title="preview.error_message ? ('Error: ' + preview.error_message) || 'There is an error with this entry.' : ''">
                                  <td data-label="S/N" class="serial-no text-center">{{ (currentPage - 1) * pageLength +
                                    index + 1 }}</td>
                                  <td data-label="MDA" class="primary-col fw-semibold"
                                    :class="preview.error_message ? 'text-danger' : ''">
                                    {{ preview.mda }}
                                  </td>
                                  <td data-label="Economic Line Item" class="secondary-col"
                                    :class="preview.error_message ? 'text-danger' : ''">
                                    {{ preview.economic_line_item }}
                                  </td>
                                  <td data-label="Amount Budgeted" class="secondary-col fw-semibold text-end"
                                    :class="preview.error_message ? 'text-danger' : ''">
                                    {{ formatCurrency(preview.amount_budgeted || 0) }}</td>
                                  <td data-label="Revenue Amount" class="secondary-col fw-semibold text-end"
                                    :class="preview.error_message ? 'text-danger' : ''">
                                    {{ formatCurrency(preview.revenue_amount || 0) }}</td>
                                </tr>

                                <tr v-if="preview.length === 0">
                                  <td colspan="5" class="text-center text-muted py-4"> No file uploaded yet. </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <!-- Pagination Summary and Actions -->
                        <div v-if="!loadingPreview && preview.length"
                          class="pagination-info d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                          <!-- Summary -->
                          <small class="text-muted">
                            Showing
                            <strong>{{ (currentPage - 1) * pageLength + 1 }}</strong>
                            to
                            <strong>{{ Math.min(currentPage * pageLength, filteredItems.length) }}</strong>
                            of
                            <strong>{{ filteredItems.length.toLocaleString() }}</strong>
                            entries
                          </small>

                          <!-- Pagination Controls -->
                          <nav class="pagination-nav me-1" aria-label="Page navigation">
                            <ul class="pagination pagination-sm mb-0">
                              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                              </li>
                              <li v-for="(page, index) in visiblePages" :key="index" class="page-item"
                                :class="{ active: currentPage === page, disabled: page === '...' }">
                                <a v-if="page !== '...'" class="page-link" href="#" @click.prevent="changePage(page)">
                                  {{ page }}
                                </a>

                                <span v-else class="page-link">...</span>
                              </li>
                              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                              </li>
                            </ul>
                          </nav>
                        </div>

                        <!-- Save and Cancel Buttons -->
                        <div class="d-flex justify-content-center mt-3 mb-3">
                          <button class="btn btn-primary btn-success me-3" type="submit"
                            :disabled="loadingSubmit || form.fileEntryError === true">
                            <span v-if="loadingSubmit" class="spinner-border spinner-border-sm me-2" role="status"
                              aria-hidden="true"></span>
                            {{ loadingSubmit ? 'Submitting...' : 'Submit' }}
                          </button>
                          <button class="btn btn-danger" type="button" @click="handleCancel">Cancel</button>
                        </div>

                      </div>
                    </div>

                  </div>

                </div>
              </div>

            </div>

          </form>

        </div>
        <!-- Templates -->
        <div class="d-flex justify-content-start align-items-center gap-2 mt-1 mb-1 form-outer-wrapper">
          <a class="download-link d-flex justify-content-start align-items-center text-decoration-none"
            @click="downloadUploadTemplate">
            <span class="material-symbols-outlined me-1 align-items-center">
              table
            </span>
            Download Template
          </a>
          |
          <a class="download-link d-flex justify-content-start align-items-center text-decoration-none"
            @click="downloadModelData">
            Download Model types
          </a>
        </div>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import Swal from 'sweetalert2'
import { debounce } from 'lodash'

const root_api_url = process.env.VUE_APP_API_ROOT_URL

const sortKey = ref('')
const sortOrder = ref('asc')
const setSort = (key, order) => {
  sortKey.value = key
  sortOrder.value = order
  currentPage.value = 1
}
// const sortBy = (key) => {
//   if (sortKey.value === key) {
//     sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
//   } else {
//     sortKey.value = key
//     sortOrder.value = 'asc'
//   }
//   currentPage.value = 1
// }
const search = ref('');
const preview = ref([]);
const pageLength = ref(25);
const currentPage = ref(1);
const selectedFile = ref(null)
const selectedFileName = ref('')
const loadingPreview = ref(false);
const loadingSubmit = ref(false)
const fileInput = ref(null)
const numberOfFileErrors = ref(0)

const form = ref({
  id: '',
  fileName: '',
  budgetName: '',
  qssApprovalDate: '',
  year: '',
  mdaLineItemFileDtos: [],
  totalBudget: 0,
  fileEntryError: false,
  errorMessage: '',
  dirBudgetRemark: '',
  permSecRemark: '',
  commRemark: '',
  dirBudApprovalDate: '',
  permSecApprovalDate: '',
  commApprovalDate: '',
  qssId: '',
  dirBudgetId: '',
  permSecId: '',
  commId: '',
  ticketId: '',
  statusInd: 0,
})

const triggerFileInput = () => fileInput.value?.click()
const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

const filteredItems = computed(() => {
  let items = preview.value

  // Error filter
  if (showErrorsOnly.value) {
    items = items.filter(item => item.error_message)
  }

  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    items = items.filter(item =>
      item.mda.toLowerCase().includes(term) ||
      item.economic_line_item.toLowerCase().includes(term) ||
      String(item.amount_budgeted).toLowerCase().includes(term) ||
      String(item.revenue_amount).toLowerCase().includes(term)
    )
  }

  if (sortKey.value) {
    items = [...items].sort((a, b) => {
      const aVal = a[sortKey.value]
      const bVal = b[sortKey.value]
      const aNum = Number(aVal)
      const bNum = Number(bVal)
      const isNumeric = !isNaN(aNum) && !isNaN(bNum)

      let result = isNumeric
        ? aNum - bNum
        : String(aVal).localeCompare(String(bVal))

      return sortOrder.value === 'asc' ? result : -result
    })
  }

  return items
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const visiblePages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const delta = 2 // pages around current

  const range = []
  const rangeWithDots = []

  let l

  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i)
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1)
      } else if (i - l > 2) {
        rangeWithDots.push('...')
      }
    }
    rangeWithDots.push(i)
    l = i
  }

  return rangeWithDots
})

const formatCurrency = (amount) => {
  return '₦' + Number(amount || 0).toLocaleString('en-NG')
}

const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (!file) return

  const allowedTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]

  if (!allowedTypes.includes(file.type)) {
    Swal.fire({
      icon: 'warning',
      title: 'Invalid file',
      text: 'Only Excel files are allowed.',
    })
    return
  }

  selectedFile.value = file
  selectedFileName.value = file.name
}

const uploadFile = async () => {
  if (!selectedFile.value) {
    Swal.fire({
      icon: 'warning',
      title: 'No file selected',
      text: 'Please choose a file first.',
    })
    return
  }

  loadingPreview.value = true

  const token = Cookies.get('auth_token')
  const formData = new FormData()
  formData.append('file', selectedFile.value)

  try {
    const response = await axios.post(`${root_api_url}/eli/file-upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      }
    })

    form.value.id = response.data.id || ''
    form.value.fileName = selectedFile.value.name || ''
    form.value.budgetName = form.value.budgetName || ''
    form.value.qssApprovalDate = response.data.qssApprovalDate || ''
    form.value.year = response.data.year || ''
    form.value.totalBudget = response.data.totalBudget || 0
    form.value.fileEntryError = response.data.fileEntryError || ''
    form.value.errorMessage = response.data.errorMessage || ''
    form.value.dirBudgetRemark = form.value.dirBudgetRemark || ''
    form.value.permSecRemark = form.value.permSecRemark || ''
    form.value.commRemark = form.value.commRemark || ''
    form.value.dirBudApprovalDate = form.value.dirBudApprovalDate || ''
    form.value.permSecApprovalDate = form.value.permSecApprovalDate || ''
    form.value.commApprovalDate = form.value.commApprovalDate || ''
    form.value.qssId = form.value.qssId || ''
    form.value.dirBudgetId = form.value.dirBudgetId || ''
    form.value.permSecId = form.value.permSecId || ''
    form.value.commId = form.value.commId || ''
    form.value.ticketId = form.value.ticketId || ''
    form.value.statusInd = form.value.statusInd || 0

    preview.value = (response.data.mdaLineItemFileDtos || []).map(item => ({
      id: item.id,
      parent_id: item.parentId,
      mda_id: item.mdaId,
      mda: item.mdaName,
      economic_line_item_id: item.economicLineItemId,
      economic_line_item: item.eliName,
      amount_budgeted: item.amountBudgeted,
      revenue_amount: item.revenueAmount,
      org_code: item.orgCode,
      economic_code: item.economicCode,
      year: item.year,
      error_message: item.errorMessage,
    }))

    numberOfFileErrors.value = preview.value.filter(item => item.error_message).length
  } catch (error) {
    console.error('Upload failed:', error)
  } finally {
    loadingPreview.value = false
  }
}

const downloadUploadTemplate = async () => {
  const token = Cookies.get('auth_token')

  try {
    const response = await axios.get(`${root_api_url}/eli/file-upload/download/upload-template`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'MDA_Line_Item_Budget_Template.xlsx')
    document.body.appendChild(link)
    link.click()
  } catch (error) {
    console.error('Download failed:', error)
    Swal.fire({
      icon: 'error',
      title: 'Download Failed',
      text: 'An error occurred while downloading the template.',
    })
  }
}

const downloadModelData = async () => {
  const token = Cookies.get('auth_token')

  try {
    const response = await axios.get(`${root_api_url}/eli/file-upload/download/model-data`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'MDA_Line_Item_Budget_Model.xlsx')
    document.body.appendChild(link)
    link.click()
  } catch (error) {
    console.error('Download failed:', error)
    Swal.fire({
      icon: 'error',
      title: 'Download Failed',
      text: 'An error occurred while downloading the model data types.',
    })
  }
}

const showErrorsOnly = ref(false)

const showErrors = () => { showErrorsOnly.value = true }
const hideErrors = () => { showErrorsOnly.value = false }

const handleSubmit = async () => {
  const token = Cookies.get('auth_token');
  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  loadingSubmit.value = true;

  if (!preview.value.length) {
    Swal.fire({
      icon: 'warning',
      title: 'No data',
      text: 'Upload a file first.',
    })
    return
  }

  form.value.id = form.value.id || ''
  form.value.fileName = selectedFile.value.name || ''
  form.value.budgetName = form.value.budgetName || ''
  form.value.qssApprovalDate = form.value.qssApprovalDate || ''
  form.value.year = form.value.year || ''
  form.value.totalBudget = form.value.totalBudget || 0
  form.value.fileEntryError = form.value.fileEntryError || ''
  form.value.dirBudgetRemark = form.value.dirBudgetRemark || ''
  form.value.permSecRemark = form.value.permSecRemark || ''
  form.value.commRemark = form.value.commRemark || ''
  form.value.dirBudApprovalDate = form.value.dirBudApprovalDate || ''
  form.value.permSecApprovalDate = form.value.permSecApprovalDate || ''
  form.value.commApprovalDate = form.value.commApprovalDate || ''
  form.value.qssId = form.value.qssId || ''
  form.value.dirBudgetId = form.value.dirBudgetId || ''
  form.value.permSecId = form.value.permSecId || ''
  form.value.commId = form.value.commId || ''
  form.value.ticketId = form.value.ticketId || ''
  form.value.statusInd = form.value.statusInd || 0

  form.value.mdaLineItemFileDtos = preview.value.map(item => ({
    id: item.id,
    parentId: item.parent_id,
    mdaId: item.mda_id,
    mdaName: item.mda,
    economicLineItemId: item.economic_line_item_id,
    eliName: item.economic_line_item,
    amountBudgeted: item.amount_budgeted,
    revenueAmount: item.revenue_amount,
    orgCode: item.org_code,
    economicCode: item.economic_code,
    year: item.year,
    errorMessage: item.error_message,
  }))

  try {
    const result = await axios.post(`${root_api_url}/eli/file-upload/save-file`, form.value, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('Save successful:', result)

    Swal.fire({
      html: `${iconHtmlSuccess}
      <h1 style="font-weight:600;">Success</h1>
      <p>MDA Budget submitted successfully.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  } catch (error) {
    preview.value = []
    form.value = ''
    selectedFile.value = null
    selectedFileName.value = ''
    currentPage.value = 1
    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${error.response}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
      console.error('Unexpected error:', error)
    } else {
      Swal.fire({
        html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>Unexpected error occurred while submitting MDA Budget. Kindly contact adminstrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
      console.error('Unexpected error:', error)
    }
  } finally {
    loadingSubmit.value = false;
    showErrorsOnly.value = false
    preview.value = []
    form.value = ''
    selectedFile.value = null
    selectedFileName.value = ''
    currentPage.value = 1
  }
}

const handleCancel = async () => {
  showErrorsOnly.value = false
  preview.value = []
  form.value = ''
  selectedFile.value = null
  selectedFileName.value = ''
  currentPage.value = 1
}

onMounted(() => {
  document.title = "Upload MDA Line Item Budget"
})

</script>
<style scoped>
.view-only-input {
  background-color: transparent;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
}

.dashboard-layout .form-outer-wrapper a {
  text-decoration: underline;
  text-decoration-style: italic;
  color: #667eea;
  font-weight: 600;
}

.preview-area {
  min-height: 0px;
}

.loading-state {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

.form-outer {
  width: 100%;
  max-width: 100% !important;
}

.preview {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  background-color:var(--table-bg-head);
}

.budget-title {
  font-weight: 600;
  font-size: 1.25rem !important;
}

.responsive-table {
  width: 100%;
}

.responsive-table td,
.responsive-table th {
  vertical-align: middle;
}

/* MDA column */
.primary-col {
  max-width: 350px;
  white-space: normal;
  word-break: break-word;
}

/* Economic Line Item column */
.secondary-col {
  max-width: 350px;
  white-space: normal;
  word-break: break-word;
}

/* Amount columns (keep clean and tight) */
.text-end {
  white-space: nowrap;
  min-width: 120px;
}

.sortable-header .th-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sort-icons {
  display: flex;
  flex-direction: column;
  margin-left: auto;
}

.sort-icon {
  cursor: pointer;
  font-size: 18px;
  line-height: 0.8;
  user-select: none;
}

.error-icon {
  font-size: 18px;
  line-height: 0.8;
}

.file-errors {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.show-errors {
  cursor: pointer;
}

.sort-icon.active {
  color: #0d6efd;
}

.download-link {
  cursor: pointer;
}

/* Responsive adjustments */
@media (max-width: 768px) {

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
